import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Typography,
  Popover,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useAuthStore } from "../stores/authStore";
import { loginService } from "../services/loginService";
import { User } from "../interfaces/DTO/user";
import { ChangePasswordDialog } from "../components/Users/ChangePassword";
import { formatName } from "../utils/namesFormater";
import { useNavigate } from "react-router-dom";

// צבע דטרמיניסטי לפי מחרוזת
function stringToColor(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += (`00${value.toString(16)}`).slice(-2);
  }
  return color;
}

// ראשי תיבות עד 2 תווים (תומך בעברית/רווחים)
function getInitials(fullName: string) {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  return parts.slice(0, 2).map(p => p[0]).join("").toUpperCase();
}

export const AuthUserDetails: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const user: User = useAuthStore((state) => state.user);
  const auth = useAuthStore((state) => state.auth);
  const isImpersonating = !!auth?.impersonatedBy;

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await loginService.logoutUser();
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const handleProfileClick = () => {
    navigate("/profile");
    handlePopoverClose();
  };

  if (!user) return null;

  const isOpen = Boolean(anchorEl);
  const displayName = formatName(user) || user.username || "";
  const initials = getInitials(displayName);
  const avatarColor = stringToColor(displayName);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        bgcolor: isImpersonating ? "#fff59d" : "transparent", // פס צהוב קל בכרטיסייה כאשר מתחזים
        px: 1,
        borderRadius: 1,
      }}
    >
      <Button onClick={handleAvatarClick} sx={{ textTransform: "none" }}>
        <Avatar
          // ללא src - בלי שירות חיצוני
          alt="User Avatar"
          sx={{ width: 40, height: 40, bgcolor: avatarColor, color: "#fff" }}
        >
          {initials || "?"}
        </Avatar>
        {!isMobile && (
          <Box sx={{ ml: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.1 }}>
            <Typography sx={{ ml: 1, fontWeight: "bold", color: "#fff" }}>
              {user.username}
            </Typography>

            <Typography variant="subtitle2" sx={{ color: "#fff" }}>
              {displayName}
            </Typography>
          </Box>
        )}
      </Button>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        // ✅ רקע שונה לדיאלוג בעת התחזות
        PaperProps={{
          sx: {
            bgcolor: isImpersonating ? "#fff8e1" : "background.paper",
            border: isImpersonating ? "1px solid #ffb300" : undefined,
          },
          elevation: isImpersonating ? 6 : 3,
        }}
      >
        <Box sx={{ p: 2, width: 250, direction: "rtl" }}>
          <Typography variant="h6" gutterBottom>
            {displayName}
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="תפקיד" secondary={user.role} />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="סוג"
                secondary={user.user_type || "לא זמין"}
              />
            </ListItem>

            <ListItem>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setDialogOpen(true)}
              >
                שינוי סיסמה
              </Button>
              <ChangePasswordDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
              />
            </ListItem>
            <ListItem>
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={handleProfileClick}
                sx={{
                  mt: 1,
                  backgroundColor: "#f5f5f5",
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                  },
                }}
              >
                מעבר לפרופיל
              </Button>
            </ListItem>
          </List>
          <Button
            variant="contained"
            color="error"
            fullWidth
            sx={{ mt: 2 }}
            onClick={handleLogout}
          >
            Logout
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};
